import React, { useState } from 'react';
import { X, Share2, Sparkles } from 'lucide-react';
import type { Outcome } from '../../types/outcomes';
import { masterRegistryEntries } from '../../data/masterRegistryEntries';

interface ShareAsStoryModalProps {
  outcome: Outcome;
  isOpen: boolean;
  onClose: () => void;
  onShare: (storyData: {
    title: string;
    frameworkId: string;
    frameworkName: string;
    challenge: string;
    application: string;
    outcomeText: string;
    lessonsLearned: string[];
    tags: string[];
  }) => void;
}

export const ShareAsStoryModal: React.FC<ShareAsStoryModalProps> = ({
  outcome,
  isOpen,
  onClose,
  onShare,
}) => {
  const [title, setTitle] = useState(`How I Used ${outcome.frameworkApplications?.[0]?.frameworkName || 'Frameworks'} to ${outcome.title}`);
  const [challenge, setChallenge] = useState(outcome.description);
  const [application, setApplication] = useState('');
  const [outcomeText, setOutcomeText] = useState(
    outcome.status === 'completed'
      ? `Successfully ${outcome.title.toLowerCase()} with ${outcome.progressPercentage}% completion.`
      : `Currently working on ${outcome.title.toLowerCase()} (${outcome.progressPercentage}% complete).`
  );
  const [lessonsLearned, setLessonsLearned] = useState<string>('');
  const [selectedFramework, setSelectedFramework] = useState(outcome.frameworkApplications?.[0]?.frameworkId || '');
  const [tags, setTags] = useState<string>(outcome.category || '');

  const appliedFrameworks = outcome.frameworkApplications || [];
  const frameworkOptions = appliedFrameworks.length > 0
    ? appliedFrameworks
    : masterRegistryEntries.slice(0, 10); // Fallback to first 10 frameworks

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !challenge.trim() || !application.trim() || !selectedFramework) return;

    const framework = appliedFrameworks.find(f => f.frameworkId === selectedFramework)
      || masterRegistryEntries.find(e => e.encodedId === selectedFramework);

    const lessons = lessonsLearned
      .split('\n')
      .map(l => l.trim())
      .filter(l => l.length > 0);

    const tagArray = tags
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    onShare({
      title: title.trim(),
      frameworkId: selectedFramework,
      frameworkName: framework
        ? ('frameworkName' in framework ? framework.frameworkName : framework.canonicalName)
        : 'Unknown Framework',
      challenge: challenge.trim(),
      application: application.trim(),
      outcomeText: outcomeText.trim(),
      lessonsLearned: lessons,
      tags: tagArray,
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-coral-500 to-amber-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Share2 className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Share as Team Story</h2>
                <p className="text-sm opacity-90">Help your team learn from your experience</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-[var(--bg-raised)]/20 transition-colors"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          <div className="space-y-6">
            {/* AI Suggestion */}
            <div className="bg-primary-50 border-2 border-primary-200 p-4">
              <div className="flex items-start gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-primary-900">We've pre-filled this story based on your outcome</p>
                  <p className="text-xs text-primary-700 mt-1">Feel free to edit any field to tell your story better</p>
                </div>
              </div>
            </div>

            {/* Story Title */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Story Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none"
                placeholder="e.g., How DEGAP Transformed Our Q1 Planning"
                required
              />
            </div>

            {/* Framework Selection */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Primary Framework Used *
              </label>
              <select
                value={selectedFramework}
                onChange={(e) => setSelectedFramework(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none"
                required
              >
                <option value="">Select a framework...</option>
                {appliedFrameworks.map(fw => (
                  <option key={fw.frameworkId} value={fw.frameworkId}>
                    {fw.frameworkName}
                  </option>
                ))}
                {appliedFrameworks.length === 0 && frameworkOptions.map(fw => (
                  <option key={fw.encodedId} value={fw.encodedId}>
                    {fw.canonicalName}
                  </option>
                ))}
              </select>
            </div>

            {/* Challenge/Situation */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Challenge / Situation *
              </label>
              <textarea
                value={challenge}
                onChange={(e) => setChallenge(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none resize-none"
                placeholder="What problem were you trying to solve? What was the context?"
                required
              />
              <p className="text-xs text-charcoal-500 mt-1">Describe the challenge you faced</p>
            </div>

            {/* How Framework Was Applied */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                How You Applied the Framework *
              </label>
              <textarea
                value={application}
                onChange={(e) => setApplication(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none resize-none"
                placeholder="Explain step-by-step how you used the framework..."
                required
              />
              <p className="text-xs text-charcoal-500 mt-1">Be specific about your approach</p>
            </div>

            {/* Outcome/Results */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Outcome / Results *
              </label>
              <textarea
                value={outcomeText}
                onChange={(e) => setOutcomeText(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none resize-none"
                placeholder="What results did you achieve? What changed?"
                required
              />
              <p className="text-xs text-charcoal-500 mt-1">Include metrics or specific improvements if possible</p>
            </div>

            {/* Lessons Learned */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Lessons Learned (Optional)
              </label>
              <textarea
                value={lessonsLearned}
                onChange={(e) => setLessonsLearned(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none resize-none"
                placeholder="One lesson per line:&#10;- Front-load stakeholder engagement&#10;- Data gathering is crucial&#10;- Visual tools help spot patterns"
              />
              <p className="text-xs text-charcoal-500 mt-1">What would you do differently? What worked well?</p>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Tags (Optional)
              </label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none"
                placeholder="e.g., roadmap, prioritization, stakeholder-management (comma-separated)"
              />
              <p className="text-xs text-charcoal-500 mt-1">Help others find your story</p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--rule)] bg-[var(--surface)] flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border-2 border-[var(--rule)] text-charcoal-700 hover:bg-[var(--hover-tint)] font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-gradient-to-r from-coral-600 to-amber-600 text-white hover:from-coral-700 hover:to-amber-700 font-medium transition-colors flex items-center gap-2"
          >
            <Share2 className="h-5 w-5" />
            Share Story with Team
          </button>
        </div>
      </div>
    </div>
  );
};

